import React, { useEffect, useState } from 'react';
import { Car } from 'lucide-react';

/**
 * Renders a vehicle's primary photo using a short-lived IONOS S3
 * presigned URL obtained from GET /api/vehicles/{id}/photo-url.
 *
 * Props:
 *   vehicleId  required — used to fetch the presigned URL
 *   alt        optional — defaults to 'Vehicle photo'
 *   className  optional — applied to the wrapper <div>
 *   imgClassName optional — applied to the <img> element when loaded
 *   fallbackClassName optional — applied to the fallback icon container
 *   size       optional — 'sm' | 'md' | 'lg' (controls fallback icon size)
 */
export const VehiclePhoto = ({
  vehicleId,
  alt = 'Vehicle photo',
  className = '',
  imgClassName = 'w-full h-full object-cover',
  fallbackClassName = 'bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center',
  size = 'md',
}) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!vehicleId) { setLoading(false); setFailed(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${backendUrl}/api/vehicles/${vehicleId}/photo-url`, {
          credentials: 'include',
        });
        if (!resp.ok) {
          if (!cancelled) setFailed(true);
          return;
        }
        const data = await resp.json();
        if (cancelled) return;
        const finalUrl = data.url?.startsWith('http') ? data.url : `${backendUrl}${data.url}`;
        setUrl(finalUrl);
      } catch (err) {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [vehicleId, backendUrl]);

  const iconSize = size === 'lg' ? 'h-14 w-14' : size === 'sm' ? 'h-6 w-6' : 'h-10 w-10';
  const circleSize = size === 'lg' ? 'w-24 h-24' : size === 'sm' ? 'w-10 h-10' : 'w-20 h-20';

  // Loading or failed → show gradient+icon placeholder
  if (loading || failed || !url) {
    return (
      <div className={`${fallbackClassName} ${className}`} data-testid="vehicle-photo-fallback">
        <div className={`${circleSize} rounded-full bg-primary/20 flex items-center justify-center`}>
          <Car className={`${iconSize} text-primary`} />
        </div>
      </div>
    );
  }

  return (
    <div className={className} data-testid="vehicle-photo-wrapper">
      <img
        src={url}
        alt={alt}
        className={imgClassName}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </div>
  );
};

export default VehiclePhoto;
